import React, { useState } from 'react';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { CallbackModal } from '../components/CallbackModal';
import { MapPin, Star, Scan, Languages, Calendar, MessageCircle, CheckCircle2, Plus, Minus, Sparkles, Smile, Palette, Phone } from 'lucide-react';
import { Link } from 'react-router-dom';
const BOOKING_URL = 'https://denticoclinic.patientportal.onl/bookings';
const WHATSAPP_URL = 'https://wa.me/447345197820';
const TELEGRAM_URL = 'https://t.me/dentico_clinic';
function TelegramIcon({
  className
}: {
  className?: string;
}) {
  return <svg viewBox="0 0 24 24" fill="currentColor" className={className} data-id="element-3609">
      <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" data-id="element-3610" />
    </svg>;
}
function AccordionItem({
  question,
  answer,
  isOpen,
  onToggle
}: {
  question: string;
  answer: string;
  isOpen: boolean;
  onToggle: () => void;
}) {
  return <div className="border-b border-gray-100 last:border-b-0" data-id="element-3611">
      <button onClick={onToggle} className="w-full flex items-center justify-between py-4 text-left focus:outline-none group" data-id="element-3612">
        <span className="text-lg font-medium text-gray-900 group-hover:text-teal-600 transition-colors" data-id="element-3613">
          {question}
        </span>
        <span className={`flex-shrink-0 ml-4 w-6 h-6 rounded-full flex items-center justify-center transition-colors ${isOpen ? 'bg-teal-600 text-white' : 'bg-gray-100 text-gray-400'}`} data-id="element-3614">
          {isOpen ? <Minus className="w-4 h-4" data-id="element-3615" /> : <Plus className="w-4 h-4" data-id="element-3616" />}
        </span>
      </button>
      <div className={`overflow-hidden transition-all duration-300 ease-in-out ${isOpen ? 'max-h-96 opacity-100 pb-4' : 'max-h-0 opacity-0'}`} data-id="element-3617">
        <p className="text-gray-600 leading-relaxed" data-id="element-3618">{answer}</p>
      </div>
    </div>;
}
export function VeneersLondonPage() {
  const [isCallbackOpen, setIsCallbackOpen] = useState(false);
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(0);
  const toggleFaq = (index: number) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index);
  };
  const faqs = [{
    q: 'Скільки коштують вініри в Лондоні?',
    a: 'Ціна залежить від матеріалу (кераміка чи композит), кількості зубів та складності роботи. Після консультації ми пропонуємо детальний план лікування та кошторис.'
  }, {
    q: 'Чи потрібно обточувати зуби під вініри?',
    a: 'У більшості випадків потрібна мінімальна підготовка емалі (0.3-0.5 мм), але обсяг залежить від клінічної ситуації. Ми завжди обираємо максимально щадний підхід (minimally invasive).'
  }, {
    q: 'Скільки служать вініри?',
    a: 'Керамічні вініри можуть служити 10-15 років і більше при правильному догляді. Термін служби залежить від матеріалу, прикусу та гігієни. На консультації ми пояснюємо, як продовжити довговічність.'
  }, {
    q: 'Чи можна зробити природний результат?',
    a: 'Так, це наша спеціалізація. Ми підбираємо форму, текстуру та колір індивідуально, щоб усмішка виглядала гармонійно і натурально, а не штучно.'
  }, {
    q: 'Яка різниця між порцеляновими та композитними вінірами?',
    a: 'Порцелянові (керамічні) вініри міцніші, довговічніші та стійкіші до фарбування. Композитні вініри (bonding) доступніші за ціною і часто встановлюються за один візит, але потребують частішого полірування.'
  }, {
    q: 'Скільки візитів потрібно для вінірів?',
    a: 'Зазвичай 2-3 візити для керамічних вінірів: консультація, підготовка/відбитки, встановлення. Композитні вініри часто можна зробити за один візит.'
  }, {
    q: 'Чи можна отримати консультацію українською?',
    a: 'Так, у Dentico Clinic можливе спілкування українською мовою під час консультації та лікування.'
  }, {
    q: 'Де знаходиться Dentico Clinic?',
    a: 'Angel / Islington, 360–364 City Road, London EC1V 2PY. 5 хвилин пішки від станції Angel.'
  }];
  // Schemas
  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(faq => ({
      '@type': 'Question',
      name: faq.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.a
      }
    }))
  };
  const procedureSchema = {
    '@context': 'https://schema.org',
    '@type': 'MedicalProcedure',
    name: 'Dental Veneers',
    description: 'Cosmetic dental procedure to improve the appearance of teeth using veneers.',
    procedureType: 'http://schema.org/NoninvasiveProcedure',
    provider: {
      '@type': 'Dentist',
      name: 'Dentico Clinic',
      address: {
        '@type': 'PostalAddress',
        streetAddress: '360-364 City Road',
        addressLocality: 'London',
        postalCode: 'EC1V 2PY',
        addressCountry: 'GB'
      }
    }
  };
  return <Layout data-id="element-3619">
      <CallbackModal isOpen={isCallbackOpen} onClose={() => setIsCallbackOpen(false)} data-id="element-3620" />

      {/* Structured Data */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{
      __html: JSON.stringify(faqSchema)
    }} data-id="element-3621" />
      <script type="application/ld+json" dangerouslySetInnerHTML={{
      __html: JSON.stringify(procedureSchema)
    }} data-id="element-3622" />

      {/* 1. HERO SECTION */}
      <section className="relative bg-white overflow-hidden" data-id="element-3623">
        <div className="absolute inset-0 bg-teal-50/50 -skew-y-3 origin-top-left transform translate-y-20" data-id="element-3624" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-16 lg:pt-20 lg:pb-24 relative" data-id="element-3625">
          <div className="grid lg:grid-cols-2 gap-12 items-center" data-id="element-3626">
            <div className="space-y-6" data-id="element-3627">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-teal-100 text-teal-800 text-sm font-medium" data-id="element-3628">
                <span className="relative flex h-2 w-2" data-id="element-3629">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-teal-400 opacity-75" data-id="element-3630"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-teal-500" data-id="element-3631"></span>
                </span>
                Естетична стоматологія
              </div>

              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight tracking-tight" data-id="element-3632">
                Вініри у Лондоні –{' '}
                <span className="text-teal-600" data-id="element-3633">Dentico Clinic</span>
                <br data-id="element-3634" />
                <span className="text-2xl sm:text-3xl font-medium text-gray-500 mt-2 block" data-id="element-3635">
                  (Angel, Islington)
                </span>
              </h1>

              <p className="text-lg text-gray-600 max-w-lg leading-relaxed" data-id="element-3636">
                Створіть ідеальну посмішку за допомогою керамічних вінірів або
                композитного бондингу. Природний результат, мінімальне
                втручання, консультація українською.
              </p>

              {/* Trust Badges */}
              <div className="flex flex-wrap gap-3 text-sm font-medium text-gray-700" data-id="element-3637">
                <div className="flex items-center gap-1.5 bg-white px-3 py-1.5 rounded-lg border border-gray-200 shadow-sm" data-id="element-3638">
                  <Star className="w-4 h-4 text-yellow-400 fill-current" data-id="element-3639" />
                  Smile Makeover
                </div>
                <div className="flex items-center gap-1.5 bg-white px-3 py-1.5 rounded-lg border border-gray-200 shadow-sm" data-id="element-3640">
                  <MapPin className="w-4 h-4 text-teal-600" data-id="element-3641" />
                  Angel, EC1V
                </div>
                <div className="flex items-center gap-1.5 bg-white px-3 py-1.5 rounded-lg border border-gray-200 shadow-sm" data-id="element-3642">
                  <Languages className="w-4 h-4 text-teal-600" data-id="element-3643" />
                  Українська мова
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 pt-4" data-id="element-3644">
                <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto" data-id="element-3645">
                  <Button size="lg" className="w-full gap-2 shadow-lg shadow-teal-600/20" data-id="element-3646">
                    <Calendar className="w-5 h-5" data-id="element-3647" />
                    Записатись на консультацію
                  </Button>
                </a>
                <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto" data-id="element-3648">
                  <Button variant="outline" size="lg" className="w-full gap-2 border-teal-200 text-teal-700 hover:bg-teal-50" data-id="element-3649">
                    <MessageCircle className="w-5 h-5 text-green-600" data-id="element-3650" />
                    WhatsApp
                  </Button>
                </a>
              </div>
            </div>

            <div className="relative" data-id="element-3651">
              <div className="absolute inset-0 bg-teal-200 rounded-3xl transform rotate-3 blur-sm opacity-30" data-id="element-3652"></div>
              <img src="https://images.unsplash.com/photo-1579684385127-1ef15d508118?auto=format&fit=crop&w=1200&q=80" alt="Вініри та естетична стоматологія - Dentico Clinic" className="relative rounded-3xl shadow-2xl object-cover h-[300px] sm:h-[400px] lg:h-[500px] w-full" data-id="element-3653" />
            </div>
          </div>
        </div>
      </section>

      {/* 2. WHAT ARE VENEERS */}
      <section className="py-16 bg-white" data-id="element-3654">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3655">
          <div className="text-center mb-10" data-id="element-3656">
            <h2 className="text-3xl font-bold text-gray-900 mb-6" data-id="element-3657">
              Що таке вініри?
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed" data-id="element-3658">
              Вініри — це ультратонкі керамічні накладки, які фіксуються на
              передню поверхню зубів для покращення їх форми, кольору та
              положення. Це ідеальне рішення для корекції сколів, проміжків між
              зубами (діастем), зміни кольору емалі, який не піддається
              відбілюванню, або вирівнювання незначних нерівностей.
            </p>
            <p className="text-lg text-gray-600 leading-relaxed mt-4" data-id="element-3659">
              Сучасні вініри виглядають абсолютно природно завдяки здатності
              пропускати світло так само, як натуральна емаль. У Dentico Clinic
              ми використовуємо цифрові технології для моделювання вашої
              майбутньої посмішки ще до початку лікування.
            </p>
          </div>
        </div>
      </section>

      {/* 3. PROCESS */}
      <section className="py-16 bg-gray-50" data-id="element-3660">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3661">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center" data-id="element-3662">
            Шлях до нової посмішки
          </h2>
          <div className="grid md:grid-cols-4 gap-8" data-id="element-3663">
            {[{
            step: '01',
            title: 'Консультація',
            desc: 'Обговорення побажань, фотопротокол та цифрове моделювання посмішки.'
          }, {
            step: '02',
            title: 'Планування',
            desc: 'Примірка макету (mock-up), щоб ви побачили результат до початку робіт.'
          }, {
            step: '03',
            title: 'Підготовка',
            desc: 'Мінімальна обробка зубів та зняття точних відбитків або сканування.'
          }, {
            step: '04',
            title: 'Фіксація',
            desc: 'Встановлення готових керамічних вінірів та фінальна естетика.'
          }].map((item, idx) => <Card key={idx} className="p-6 relative overflow-hidden" data-id="element-3664">
                <div className="text-6xl font-bold text-teal-50 absolute -top-4 -right-4 select-none" data-id="element-3665">
                  {item.step}
                </div>
                <div className="relative z-10" data-id="element-3666">
                  <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-3667">
                    {item.title}
                  </h3>
                  <p className="text-gray-600" data-id="element-3668">{item.desc}</p>
                </div>
              </Card>)}
          </div>
        </div>
      </section>

      {/* 4. TYPES */}
      <section className="py-16 bg-white" data-id="element-3669">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3670">
          <div className="grid lg:grid-cols-2 gap-12 items-center" data-id="element-3671">
            <div data-id="element-3672">
              <h2 className="text-3xl font-bold text-gray-900 mb-6" data-id="element-3673">
                Види естетичної реставрації
              </h2>
              <div className="space-y-6" data-id="element-3674">
                <div className="flex gap-4" data-id="element-3675">
                  <div className="p-3 bg-teal-50 rounded-lg text-teal-600 h-fit" data-id="element-3676">
                    <Sparkles className="w-6 h-6" data-id="element-3677" />
                  </div>
                  <div data-id="element-3678">
                    <h3 className="text-xl font-bold text-gray-900 mb-2" data-id="element-3679">
                      Керамічні вініри
                    </h3>
                    <p className="text-gray-600" data-id="element-3680">
                      Виготовляються в лабораторії з високоміцної кераміки.
                      Максимально естетичні, довговічні та стійкі до зміни
                      кольору.
                    </p>
                  </div>
                </div>
                <div className="flex gap-4" data-id="element-3681">
                  <div className="p-3 bg-teal-50 rounded-lg text-teal-600 h-fit" data-id="element-3682">
                    <Palette className="w-6 h-6" data-id="element-3683" />
                  </div>
                  <div data-id="element-3684">
                    <h3 className="text-xl font-bold text-gray-900 mb-2" data-id="element-3685">
                      Композитний бондинг
                    </h3>
                    <p className="text-gray-600" data-id="element-3686">
                      Художня реставрація композитним матеріалом прямо в кріслі.
                      Швидше та доступніше, але потребує періодичного
                      полірування.
                    </p>
                  </div>
                </div>
                <div className="flex gap-4" data-id="element-3687">
                  <div className="p-3 bg-teal-50 rounded-lg text-teal-600 h-fit" data-id="element-3688">
                    <Smile className="w-6 h-6" data-id="element-3689" />
                  </div>
                  <div data-id="element-3690">
                    <h3 className="text-xl font-bold text-gray-900 mb-2" data-id="element-3691">
                      Smile Makeover
                    </h3>
                    <p className="text-gray-600" data-id="element-3692">
                      Комплексне перевтілення посмішки, яке може поєднувати
                      вініри, коронки та відбілювання для ідеального результату.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="relative h-[400px] rounded-2xl overflow-hidden shadow-xl" data-id="element-3693">
              <img src="https://images.unsplash.com/photo-1606811841689-23dfddce3e95?auto=format&fit=crop&w=1200&q=80" alt="Типи вінірів" className="absolute inset-0 w-full h-full object-cover" data-id="element-3694" />
            </div>
          </div>
        </div>
      </section>

      {/* 5. BENEFITS */}
      <section className="py-16 bg-teal-50" data-id="element-3695">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3696">
          <div className="text-center mb-12" data-id="element-3697">
            <h2 className="text-3xl font-bold text-gray-900 mb-4" data-id="element-3698">
              Чому варто обрати Dentico Clinic
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-3699">
            {[{
            title: 'Природний вигляд',
            desc: 'Ми створюємо посмішки, які виглядають натурально, а не штучно.'
          }, {
            title: 'Мінімальне втручання',
            desc: 'Зберігаємо максимум здорових тканин зуба завдяки сучасним технікам.'
          }, {
            title: 'Українська мова',
            desc: 'Легке спілкування та повне розуміння ваших побажань.'
          }, {
            title: 'Зручна локація',
            desc: 'Клініка в центрі Лондона (Angel), легко дістатися з будь-якого району.'
          }].map((item, idx) => <Card key={idx} className="p-6 bg-white text-center" data-id="element-3700">
                <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center text-teal-600 mb-4 mx-auto" data-id="element-3701">
                  <CheckCircle2 className="w-6 h-6" data-id="element-3702" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2" data-id="element-3703">
                  {item.title}
                </h3>
                <p className="text-gray-600" data-id="element-3704">{item.desc}</p>
              </Card>)}
          </div>
        </div>
      </section>

      {/* MID-PAGE CTA */}
      <section className="py-12 bg-teal-600" data-id="element-3705">
        <div className="max-w-4xl mx-auto px-4 text-center" data-id="element-3706">
          <h2 className="text-2xl lg:text-3xl font-bold text-white mb-4" data-id="element-3707">
            Хочете побачити, як виглядатиме ваша нова посмішка?
          </h2>
          <p className="text-teal-100 mb-6" data-id="element-3708">
            Запишіться на консультацію — ми покажемо цифровий макет результату.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-3" data-id="element-3709">
            <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" data-id="element-3710">
              <Button size="lg" className="gap-2 bg-white text-teal-900 hover:bg-teal-50" data-id="element-3711">
                <Calendar className="w-5 h-5" data-id="element-3712" />
                Записатись онлайн
              </Button>
            </a>
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" data-id="element-3713">
              <Button variant="outline" size="lg" className="gap-2 border-white text-white hover:bg-white/10" data-id="element-3714">
                <MessageCircle className="w-5 h-5" data-id="element-3715" />
                WhatsApp
              </Button>
            </a>
            <Button variant="outline" size="lg" className="gap-2 border-white text-white hover:bg-white/10" onClick={() => setIsCallbackOpen(true)} data-id="element-3716">
              <Phone className="w-5 h-5" data-id="element-3717" />
              Замовити дзвінок
            </Button>
          </div>
        </div>
      </section>

      {/* 6. FAQ */}
      <section className="py-16 bg-white" data-id="element-3718">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3719">
          <div className="text-center mb-10" data-id="element-3720">
            <h2 className="text-3xl font-bold text-gray-900 mb-4" data-id="element-3721">
              Часті запитання про вініри
            </h2>
          </div>
          <div className="space-y-2" data-id="element-3722">
            {faqs.map((faq, idx) => <AccordionItem key={idx} question={faq.q} answer={faq.a} isOpen={openFaqIndex === idx} onToggle={() => toggleFaq(idx)} data-id="element-3723" />)}
          </div>
        </div>
      </section>

      {/* 7. INTERNAL LINKS */}
      <section className="py-12 bg-gray-50 border-t border-gray-100" data-id="element-3724">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3725">
          <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-3726">
            Дивіться також
          </h2>
          <div className="flex flex-wrap gap-4" data-id="element-3727">
            <Link to="/ua/ukrainskyi-stomatolog-london" data-id="element-3728">
              <Button variant="outline" size="sm" data-id="element-3729">
                Український стоматолог
              </Button>
            </Link>
            <Link to="/ua/implanty-london" data-id="element-3730">
              <Button variant="outline" size="sm" data-id="element-3731">
                Імплантація
              </Button>
            </Link>
            <Link to="/ua/root-canal-london" data-id="element-3732">
              <Button variant="outline" size="sm" data-id="element-3733">
                Лікування каналів
              </Button>
            </Link>
            <Link to="/ua/emergency-dentist-london" data-id="element-3734">
              <Button variant="outline" size="sm" data-id="element-3735">
                Невідкладна допомога
              </Button>
            </Link>
            <Link to="/ua/services/smile-makeover" data-id="element-3736">
              <Button variant="outline" size="sm" data-id="element-3737">
                Smile Makeover
              </Button>
            </Link>
            <Link to="/ua/prices" data-id="element-3738">
              <Button variant="outline" size="sm" data-id="element-3739">
                Ціни
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* 8. CTA */}
      <section className="py-16 bg-teal-900" data-id="element-3740">
        <div className="max-w-4xl mx-auto px-4 text-center" data-id="element-3741">
          <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4" data-id="element-3742">
            Мрієте про ідеальну посмішку?
          </h2>
          <p className="text-xl text-teal-100 mb-8 max-w-2xl mx-auto" data-id="element-3743">
            Запишіться на консультацію та дізнайтеся, як вініри можуть змінити
            ваше життя.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4" data-id="element-3744">
            <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto" data-id="element-3745">
              <Button size="lg" className="w-full gap-2 bg-white text-teal-900 hover:bg-teal-50" data-id="element-3746">
                <Calendar className="w-5 h-5" data-id="element-3747" />
                Записатись онлайн
              </Button>
            </a>
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto" data-id="element-3748">
              <Button variant="outline" size="lg" className="w-full gap-2 border-teal-400 text-teal-100 hover:bg-teal-800 hover:text-white" data-id="element-3749">
                <MessageCircle className="w-5 h-5" data-id="element-3750" />
                WhatsApp
              </Button>
            </a>
            <div className="w-full sm:w-auto" data-id="element-3751">
              <Button variant="outline" size="lg" className="w-full gap-2 border-teal-400 text-teal-100 hover:bg-teal-800 hover:text-white" onClick={() => setIsCallbackOpen(true)} data-id="element-3752">
                <Phone className="w-5 h-5" data-id="element-3753" />
                Замовити дзвінок
              </Button>
            </div>
          </div>
        </div>
      </section>
    </Layout>;
}